import React, { useState } from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/footer.scss";

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

function Footer() {
  const [email, setEmail] = useState("");
  const [newsletterMessage, setNewsletterMessage] = useState({
    type: "idle",
    text: "",
  });

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNewsletterSubmit = (event) => {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();

    if (!isValidEmail(normalizedEmail)) {
      setNewsletterMessage({
        type: "error",
        text: "Enter a valid email address.",
      });
      return;
    }

    let subscribers = [];
    try {
      const storedSubscribers = window.localStorage.getItem(
        siteConfig.storageKeys.newsletter
      );
      const parsedSubscribers = storedSubscribers
        ? JSON.parse(storedSubscribers)
        : [];
      subscribers = Array.isArray(parsedSubscribers) ? parsedSubscribers : [];
    } catch {
      subscribers = [];
    }

    const isAlreadySubscribed = subscribers.includes(normalizedEmail);
    if (!isAlreadySubscribed) {
      try {
        window.localStorage.setItem(
          siteConfig.storageKeys.newsletter,
          JSON.stringify([...subscribers, normalizedEmail])
        );
      } catch {
        // A successful local interaction does not depend on storage support.
      }
    }

    setNewsletterMessage({
      type: "success",
      text: isAlreadySubscribed
        ? "You’re already on the list."
        : "Welcome to the adventure. Check your inbox soon.",
    });
    setEmail("");
  };

  return (
    <footer className="site-footer" role="contentinfo">
      <section
        className="site-footer__newsletter"
        aria-labelledby="newsletter-title"
      >
        <div className="site-footer__newsletter-inner">
          <p className="site-footer__eyebrow">
            {siteConfig.newsletter.eyebrow}
          </p>
          <h2 id="newsletter-title">{siteConfig.newsletter.title}</h2>
          <p>{siteConfig.newsletter.description}</p>
          <form noValidate onSubmit={handleNewsletterSubmit}>
            <label className="sr-only" htmlFor="footer-newsletter-email">
              Email address
            </label>
            <input
              id="footer-newsletter-email"
              type="email"
              value={email}
              placeholder="Email address"
              autoComplete="email"
              aria-describedby="newsletter-message"
              onChange={(event) => {
                setEmail(event.target.value);
                if (newsletterMessage.type !== "idle") {
                  setNewsletterMessage({ type: "idle", text: "" });
                }
              }}
            />
            <button type="submit" aria-label="Join the newsletter">
              Join <span aria-hidden="true">↗</span>
            </button>
          </form>
          <p
            id="newsletter-message"
            className={`site-footer__form-message is-${newsletterMessage.type}`}
            aria-live="polite"
          >
            {newsletterMessage.text}
          </p>
        </div>
      </section>

      <div className="site-footer__inner">
        <div className="site-footer__intro">
          <Link
            className="site-footer__wordmark"
            to="/"
            aria-label={siteConfig.logo.alt}
            onClick={scrollToTop}
          >
            {siteConfig.logo.text}
          </Link>
          <p>{siteConfig.description}</p>
          <a href={`mailto:${siteConfig.contact.email}`}>
            {siteConfig.contact.email}
          </a>
          <span>{siteConfig.contact.location}</span>
        </div>

        <div className="site-footer__links" aria-label="Footer navigation">
          {siteConfig.footerNavigation.map((section) => (
            <nav key={section.title} aria-label={section.title}>
              <h2>{section.title}</h2>
              {section.links.map((link) => (
                <Link key={link.label} to={link.to} onClick={scrollToTop}>
                  {link.label}
                </Link>
              ))}
            </nav>
          ))}
        </div>

      </div>

      <div className="site-footer__bottom">
        <p>
          © {new Date().getFullYear()} {siteConfig.brandName}. All rights
          reserved.
        </p>
        <div className="site-footer__socials">
          {siteConfig.socialLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              target="_blank"
              rel="noreferrer"
            >
              {link.label}
            </a>
          ))}
        </div>
        <button type="button" onClick={scrollToTop}>
          Back to top <span aria-hidden="true">↑</span>
        </button>
      </div>
    </footer>
  );
}

export default Footer;
